import { useState, useEffect, useCallback, useRef } from 'react'
import {
  MSG_MOBILE_CONFIG_UPDATE,
  MSG_PREVIEW_TOKEN_REFRESH,
  MSG_PREVIEW_READY,
  isAllowedStorefrontOrigin,
  type MobileConfigUpdateMessage,
  type PreviewMessage,
} from './preview-protocol'

/**
 * Hook for postMessage communication with the storefront iframe(s).
 *
 * Listens for PREVIEW_READY from iframe(s), queues messages until ready,
 * then sends to ALL tracked iframe windows (PhoneFrame renders 2 iframes:
 * desktop + mobile, both mount but only one is visible at a time).
 */
export function usePostMessage(storefrontUrl: string | null) {
  const [isReady, setIsReady] = useState(false)
  const targetWindowsRef = useRef<Set<Window>>(new Set())
  const targetOriginRef = useRef<string>('')
  const queueRef = useRef<PreviewMessage[]>([])

  // Extract origin from storefront URL
  useEffect(() => {
    if (storefrontUrl) {
      try {
        targetOriginRef.current = new URL(storefrontUrl).origin
      } catch {
        // Invalid URL — leave origin empty
      }
    }
  }, [storefrontUrl])

  // Listen for PREVIEW_READY (may arrive from multiple iframes)
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (!isAllowedStorefrontOrigin(event.origin, targetOriginRef.current)) return
      if (event.data?.type === MSG_PREVIEW_READY) {
        const source = event.source as Window
        targetWindowsRef.current.add(source)
        setIsReady(true)

        // Flush queued messages to this newly-ready window, then clear
        const origin = targetOriginRef.current || event.origin
        for (const msg of queueRef.current) {
          source.postMessage(msg, origin)
        }
        queueRef.current = []
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  const sendMessage = useCallback((msg: PreviewMessage) => {
    const origin = targetOriginRef.current
    if (targetWindowsRef.current.size > 0 && origin) {
      for (const win of targetWindowsRef.current) {
        try {
          win.postMessage(msg, origin)
        } catch {
          // Window may have been closed/navigated — remove it
          targetWindowsRef.current.delete(win)
        }
      }
    } else {
      queueRef.current.push(msg)
    }
  }, [])

  const sendConfigUpdate = useCallback(
    (config: MobileConfigUpdateMessage['config']) => {
      sendMessage({ type: MSG_MOBILE_CONFIG_UPDATE, config })
    },
    [sendMessage]
  )

  const sendTokenRefresh = useCallback(
    (token: string) => {
      sendMessage({ type: MSG_PREVIEW_TOKEN_REFRESH, token })
    },
    [sendMessage]
  )

  return { isReady, sendConfigUpdate, sendTokenRefresh }
}
